import React, { useCallback, useEffect, useState } from 'react'
import { ConfigProvider, Drawer, Form, Select, Space, Tooltip, Button } from 'antd'
import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useService } from '@pushly/aqe/lib/hooks'
import { useRefEffect } from '@pushly/aqe/lib/hooks/use-ref-effect'
import TemplatizedTextField from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import { CreateEventWebhookRequestDto } from '@pushly/models/lib/structs/event-webhooks/create-event-webhook-request-dto'
import { UpdateEventWebhookRequestDto } from '@pushly/models/lib/structs/event-webhooks/update-event-webhook-request-dto'
import { EventWebhook } from '@pushly/models/lib/structs/event-webhooks/event-webhook'
import { WebhookEventType } from '@pushly/models/lib/enums/webhook-event-type'
import { DomainWebhookService } from '../../services/domain-webhook.service'
import { ApiVersion } from '../../enums/api-version.enum'
import { simpleNotification } from '../../_utils/utils'
import { getSecretsMacroFields } from '../../_utils/domain'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { pascalCase } from 'change-case'

interface WebhookDrawerProps {
    domainId: number
    visible: boolean
    onClose: () => void
    onSaved: () => void
    editingWebhook: EventWebhook | null
}

const EVENT_TYPE_OPTIONS = (() => {
    const grouped: Record<string, Array<{ label: string; value: string }>> = {}
    for (const type of Object.values(WebhookEventType)) {
        const [resource, action] = type.split('.')
        const group = pascalCase(resource)
        if (!grouped[group]) grouped[group] = []
        grouped[group].push({ label: pascalCase(action), value: type })
    }
    return Object.entries(grouped).map(([group, options]) => ({
        label: group,
        options,
    }))
})()

const headersToEntries = (headers: Record<string, string> | null | undefined): Array<{ key: string; value: string }> => {
    if (!headers || Object.keys(headers).length === 0) return []
    return Object.entries(headers).map(([key, value]) => ({ key, value }))
}

const entriesToHeaders = (entries: Array<{ key: string; value: string }> | undefined): Record<string, string> | undefined => {
    if (!entries) return undefined
    const filtered = entries.filter((e) => e.key?.trim() !== '')
    if (filtered.length === 0) return undefined
    return filtered.reduce<Record<string, string>>((acc, { key, value }) => {
        acc[key.trim()] = value ?? ''
        return acc
    }, {})
}

const FieldTooltip = ({ text }: { text: string }) => (
    <Tooltip title={text}>
        <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }} />
    </Tooltip>
)

export const WebhookDrawer = (props: WebhookDrawerProps) => {
    const { domainId, visible, onClose, onSaved, editingWebhook } = props
    const webhookService = useService(DomainWebhookService)
    const [form] = Form.useForm()
    const [submitting, setSubmitting] = useState(false)
    const [urlValue, setUrlValue] = useState('')

    const isEditing = editingWebhook != null

    const [secretsMacroOptions] = useRefEffect(() => {
        return getSecretsMacroFields(domainId)
    }, [domainId])

    const resetForm = useCallback(() => {
        if (editingWebhook) {
            form.setFieldsValue({
                event_types: editingWebhook.event_types,
                headers: headersToEntries(editingWebhook.headers),
            })
            setUrlValue(editingWebhook.url ?? '')
        } else {
            form.resetFields()
            setUrlValue('')
        }
    }, [editingWebhook, form])

    useEffect(() => {
        if (visible) {
            resetForm()
        }
    }, [visible, editingWebhook])

    const handleSubmit = async () => {
        const values = form.getFieldsValue()
        const errors: string[] = []

        if (!values.event_types || values.event_types.length === 0) {
            errors.push('At least one event type is required.')
        }
        if (!urlValue?.trim()) {
            errors.push('URL is required.')
        }

        if (errors.length > 0) {
            errors.forEach((msg) => simpleNotification('error', msg))
            return
        }

        setSubmitting(true)
        try {
            const apiOptions = {
                showLoadingScreen: false,
                version: ApiVersion.V3,
            }

            const payload = {
                event_types: values.event_types,
                url: urlValue,
                headers: entriesToHeaders(values.headers),
            }

            let res
            if (isEditing) {
                const dto = UpdateEventWebhookRequestDto.from(payload as UpdateEventWebhookRequestDto)
                res = await webhookService.update(domainId, editingWebhook.id, dto, apiOptions)
            } else {
                const dto = CreateEventWebhookRequestDto.from(payload as CreateEventWebhookRequestDto)
                res = await webhookService.create(domainId, dto, apiOptions)
            }

            if (res.ok) {
                simpleNotification('success', isEditing ? 'Webhook updated.' : 'Webhook created.')
                onSaved()
                onClose()
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Drawer
            title={isEditing ? 'Edit Webhook' : 'Add Webhook'}
            placement="right"
            width={600}
            open={visible}
            onClose={onClose}
            destroyOnHidden
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button type="primary" onClick={handleSubmit} loading={submitting}>
                            {isEditing ? 'Update' : 'Create'}
                        </Button>
                    </Space>
                </div>
            }
        >
            <ConfigProvider
                theme={{
                    components: {
                        Form: {
                            itemMarginBottom: 16,
                        },
                    },
                }}
            >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                labelAlign="left"
                requiredMark={false}
            >
                <Form.Item
                    name="event_types"
                    label={
                        <span>
                            Event Types
                            <FieldTooltip text="Select which event types this webhook should receive." />
                        </span>
                    }
                >
                    <Select
                        mode="multiple"
                        placeholder="Select event types"
                        options={EVENT_TYPE_OPTIONS}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            URL
                            <FieldTooltip text="The destination URL for webhook delivery." />
                        </span>
                    }
                >
                    <TemplatizedTextField
                        placeholder="https://example.com/webhook"
                        hideEmojis={true}
                        noStyle={true}
                        hideMacros={true}
                        value={urlValue}
                        onChange={(v) => setUrlValue(v ?? '')}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Headers
                            <FieldTooltip text="Use {{secret.alias}} format to reference domain secrets for sensitive header values." />
                        </span>
                    }
                >
                    <Form.List name="headers">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                        <Form.Item {...restField} name={[name, 'key']} noStyle>
                                            <TemplatizedTextField
                                                placeholder="Header name"
                                                hideEmojis={true}
                                                hideMacros={true}
                                                value={form.getFieldValue(['headers', name, 'key'])}
                                                onChange={(v) => {
                                                    const headers = form.getFieldValue('headers') || []
                                                    headers[name] = { ...headers[name], key: v ?? '' }
                                                    form.setFieldsValue({ headers })
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item {...restField} name={[name, 'value']} noStyle>
                                            <TemplatizedTextField
                                                placeholder="Header value"
                                                hideEmojis={true}
                                                macroTypes={['secrets']}
                                                secretsMacroOptions={secretsMacroOptions.current}
                                                value={form.getFieldValue(['headers', name, 'value'])}
                                                onChange={(v) => {
                                                    const headers = form.getFieldValue('headers') || []
                                                    headers[name] = { ...headers[name], value: v ?? '' }
                                                    form.setFieldsValue({ headers })
                                                }}
                                            />
                                        </Form.Item>
                                        <Button
                                            type="text"
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => remove(name)}
                                            danger
                                        />
                                    </Space>
                                ))}
                                {fields.length < 10 && (
                                    <Button type="dashed" onClick={() => add({ key: '', value: '' })} icon={<PlusOutlined />} style={{ width: '100%' }}>
                                        Add Header
                                    </Button>
                                )}
                            </>
                        )}
                    </Form.List>
                </Form.Item>
            </Form>
            </ConfigProvider>
        </Drawer>
    )
}
